import { useCallback, useEffect, useState } from "react";
import {
  LandingGearCatalogEntry,
  LandingGearTypeCatalogItem,
} from "../../../../types/subAssembly";
import { useSubAssemblyService } from "../../../../services/subAssemblyService";

const CRJ_FAMILY_CANONICAL = "CRJ";
const EMBRAER_FAMILY_CANONICAL = "Embraer";

const CRJ_ALIASES: Record<string, string> = {
  CRJ: "CRJ",
  "CANADIAN REGIONAL JET": "Canadian Regional Jet",
  BOMBARDIER: "Bombardier",
  MHI: "MHI",
  "MITSUBISHI HEAVY INDUSTRIAL": "Mitsubishi Heavy Industrial",
};

function normalizeAirframeFamily(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return "";

  const normalized = trimmed.toUpperCase();
  if (normalized in CRJ_ALIASES) {
    return CRJ_ALIASES[normalized];
  }

  if (normalized === "EMBRAER" || normalized === "EMBARER") {
    return EMBRAER_FAMILY_CANONICAL;
  }

  return "";
}

function toCanonicalAirframeFamily(value: string): string {
  const normalized = value.trim().toUpperCase();
  if (!normalized) return "";

  if (normalized in CRJ_ALIASES) {
    return CRJ_FAMILY_CANONICAL;
  }

  if (normalized === "EMBRAER" || normalized === "EMBARER") {
    return EMBRAER_FAMILY_CANONICAL;
  }

  return "";
}

function sameAirframeFamily(left: string, right: string): boolean {
  const leftCanonical = toCanonicalAirframeFamily(left);
  const rightCanonical = toCanonicalAirframeFamily(right);

  if (!leftCanonical || !rightCanonical) {
    return false;
  }

  return leftCanonical === rightCanonical;
}

function pushUniqueCaseInsensitive(target: string[], value: string): void {
  if (!target.some((item) => sameAirframeFamily(item, value))) {
    target.push(value);
  }
}

function sortAirframeFamilies(items: string[]): string[] {
  return [...items].sort((left, right) => {
    const leftCanonical = toCanonicalAirframeFamily(left);
    const rightCanonical = toCanonicalAirframeFamily(right);

    if (leftCanonical === CRJ_FAMILY_CANONICAL && rightCanonical !== CRJ_FAMILY_CANONICAL) {
      return -1;
    }

    if (rightCanonical === CRJ_FAMILY_CANONICAL && leftCanonical !== CRJ_FAMILY_CANONICAL) {
      return 1;
    }

    if (
      leftCanonical === EMBRAER_FAMILY_CANONICAL &&
      rightCanonical !== EMBRAER_FAMILY_CANONICAL
    ) {
      return -1;
    }

    if (
      rightCanonical === EMBRAER_FAMILY_CANONICAL &&
      leftCanonical !== EMBRAER_FAMILY_CANONICAL
    ) {
      return 1;
    }

    return left.localeCompare(right, undefined, {
      sensitivity: "base",
      numeric: true,
    });
  });
}

function filterSupportedAirframeFamilies(items: string[]): string[] {
  const result: string[] = [];

  for (const item of items) {
    const normalized = normalizeAirframeFamily(String(item ?? ""));
    if (normalized) {
      pushUniqueCaseInsensitive(result, normalized);
    }
  }

  return sortAirframeFamilies(result);
}

function mapCatalogEntry(raw: any): LandingGearCatalogEntry {
  return {
    positionCode: String(
      raw.positionCode ?? raw.PositionCode ?? raw.type ?? raw.Type ?? ""
    ).toUpperCase(),
    label: String(raw.label ?? raw.Label ?? raw.type ?? raw.Type ?? ""),
    allowedSubAssemblyTypes: Array.isArray(
      raw.allowedSubAssemblyTypes ?? raw.AllowedSubAssemblyTypes
    )
      ? (raw.allowedSubAssemblyTypes ?? raw.AllowedSubAssemblyTypes).map(
          (item: unknown) => String(item)
        )
      : [],
  };
}

export function useSubAssemblyCatalog(airframeFamily?: string) {
  const { getAirframeFamilies, getLandingGearCatalog } = useSubAssemblyService();
  const [families, setFamilies] = useState<string[]>([]);
  const [landingGearTypes, setLandingGearTypes] = useState<
    LandingGearTypeCatalogItem[]
  >([]);
  const [loading, setLoading] = useState(false);

  const loadFamilies = useCallback(async () => {
    try {
      const items = await getAirframeFamilies();
      setFamilies(filterSupportedAirframeFamilies(items));
    } catch {
      setFamilies([]);
    }
  }, [getAirframeFamilies]);

  const loadCatalog = useCallback(async () => {
    if (!airframeFamily?.trim()) {
      setLandingGearTypes([]);
      return;
    }

    setLoading(true);
    try {
      const catalog = await getLandingGearCatalog(airframeFamily);
      setLandingGearTypes(
        catalog.map((entry) => ({
          type: entry.positionCode,
          label: entry.label,
          allowedSubAssemblyTypes: entry.allowedSubAssemblyTypes,
        }))
      );
    } catch {
      setLandingGearTypes([]);
    } finally {
      setLoading(false);
    }
  }, [airframeFamily, getLandingGearCatalog]);

  useEffect(() => {
    void loadFamilies();
  }, [loadFamilies]);

  useEffect(() => {
    void loadCatalog();
  }, [loadCatalog]);

  return {
    families,
    landingGearTypes,
    loading,
    reloadCatalog: loadCatalog,
  };
}

export function useAirframeFamilies() {
  const { getAirframeFamilies } = useSubAssemblyService();
  const [families, setFamilies] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    void getAirframeFamilies()
      .then((items) => {
        if (active) setFamilies(filterSupportedAirframeFamilies(items));
      })
      .catch(() => {
        if (active) setFamilies([]);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [getAirframeFamilies]);

  return { families, loading };
}

export {
  mapCatalogEntry,
  normalizeAirframeFamily,
  filterSupportedAirframeFamilies,
  toCanonicalAirframeFamily,
  sameAirframeFamily,
};
